import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { forgotPassword, setAuthError } from '../store/slices/authSlice';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Modal,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as LoginMailIcon } from '../assets/images/loginMailIcon.svg';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';
import authImage from '../assets/images/auth.png';

const PasswordRecoveryModal = ({
    open,
    email,
    onClose,
    onContinue,
}: {
    open: boolean;
    email: string;
    onClose: () => void;
    onContinue: () => void;
}) => (
    <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="password-recovery-modal-title"
        aria-describedby="password-recovery-modal-description"
    >
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                maxWidth: 'calc(100% - 2rem)',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
            }}
        >
            <Typography id="password-recovery-modal-title" variant="h6" component="h2" gutterBottom>
                Correo enviado exitosamente
            </Typography>
            <Typography id="password-recovery-modal-description" variant="body1" sx={{ mb: 2 }}>
                Hemos enviado un código de verificación a <strong>{email}</strong>. Revisa tu bandeja de entrada.
            </Typography>
            <Button
                variant="contained"
                color="primary"
                onClick={onContinue}
                sx={{ mt: 2 }}
            >
                Continuar
            </Button>
        </Box>
    </Modal>
);

const ForgotPassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isLoading, error } = useSelector((state: RootState) => state.auth);
    
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    const validateEmail = (): boolean => {
        if (!email.trim()) {
            setEmailError('El email es requerido');
            return false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Ingresa un email válido');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateEmail()) return;

        const result = await dispatch(forgotPassword(email));
        if (forgotPassword.fulfilled.match(result)) {
            setEmailSent(true);
        }
    };

    return (
        <Grid container style={{ height: "100vh", margin: "0 !important" }}>
            <Grid
                container
                item
                xs={12}
                md={6}
                direction="column"
                justifyContent="center"
                alignItems="center"
                style={{ backgroundColor: theme.s10palette?.S10Blueblue50 }}
            >
                <MySnackbar
                    open={!!error}
                    autoHideDuration={6000}
                    msg={error || ''}
                    severity="error"
                    onClose={() => dispatch(setAuthError(''))}
                />

                <Grid
                    container
                    direction="row"
                    maxWidth="20rem"
                    alignItems="center"
                    rowSpacing="1rem"
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <Grid justifyContent="center" alignItems="center" alignContent="center" item xs={12} style={{ marginTop: ".5rem" }}>
                        <img style={{ width: "8.75rem" }} src={s10Logo} alt="S10" />
                    </Grid>
                    <Typography variant="h3" style={{ marginBottom: "0.5rem" }}>
                        ¡Bienvenido!
                    </Typography>
                    <Typography variant="h6">Portal Mis Boletas - Recuperar contraseña</Typography>
                    <Divider
                        style={{
                            width: "100%",
                            marginTop: "1.19rem",
                            marginBottom: "1.5rem",
                            color: theme.s10palette.S10Blueblue100,
                        }}
                    />
                    <Typography variant="body1" align="left" width="100%">
                        Ingresa tu correo afiliado para recuperar contraseña
                    </Typography>
                    <MyTextField
                        id="email"
                        label="Usuario"
                        type="text"
                        name="email"
                        value={email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                        error={!!emailError}
                        helperText={emailError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" style={{ color: theme.s10palette.S10Greygrey900 }}>
                                    <LoginMailIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="body2" style={{ marginTop: '10px' }}>
                        <Link to="/login" color="primary">
                            Ingresa aquí
                        </Link>
                    </Typography>
                    <Grid item width="20rem" style={{ marginTop: "1rem" }}>
                        <Button variant="contained" type="submit" fullWidth disabled={isLoading}>
                            Recuperar
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid
                item
                md={6}
                sx={{ display: { xs: 'none', md: 'block' } }}
                style={{
                    backgroundColor: theme.s10palette?.S10Blueblue700,
                    backgroundImage: `url(loginDefaultImage.svg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 0,
                    }}
                >
                    <img
                        src={authImage}
                        alt="Ilustración auth"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '90%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                        }}
                    />
                </Box>
            </Grid>

            <PasswordRecoveryModal
                open={emailSent}
                email={email}
                onClose={() => navigate('/login')}
                onContinue={() => navigate('/reset-password', { state: { email } })}
            />

            {isLoading && <LoadingComponent loadingText="Enviando correo..." />}
        </Grid>
    );
};

export default ForgotPassword;